import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Lock,
  Mail,
  Monitor,
  Pause,
  RefreshCw,
  Smartphone,
  Sparkles,
  Volume2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { SiteHeader } from "@/components/SiteHeader";
import { cn } from "@/lib/utils";
import { api, ApiError, googleAuthUrl, setAccessToken } from "@/lib/api";
import { useAuth, type AuthUser } from "@/lib/auth";
import {
  creatorOnboardingSuggestCategoriesPath,
  creatorOnboardingSuggestTopicsPath,
  env,
} from "@/lib/env";
import {
  fetchCreatorConfig,
  type CreatorConfig,
  type CreatorVoice,
  type VideoStyle,
} from "@/lib/creator-config";

export const Route = createFileRoute("/onboarding")({
  head: () => ({
    meta: [
      { title: "Set up your series — AutoTubber" },
      {
        name: "description",
        content:
          "Choose Shorts or longform, then pick topics, a voice and a video style to launch your series.",
      },
      { property: "og:title", content: "Set up your series — AutoTubber" },
      {
        property: "og:description",
        content: "A guided six-step setup for your automated faceless video series.",
      },
    ],
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    auth: typeof search.auth === "string" ? search.auth : undefined,
    authError: typeof search.authError === "string" ? search.authError : undefined,
    access_token:
      typeof search.access_token === "string" ? search.access_token : undefined,
  }),
  component: Onboarding,
});

type VideoTypeId = "SHORTS" | "LONGFORM";

type OnboardingDraft = {
  videoType: VideoTypeId | null;
  category: string | null;
  custom: string;
  selectedTopics: string[];
  voiceId: string;
  narrator: string;
  videoStyle: string;
};

function LockedBanner({ children }: { children: string }) {
  return (
    <div className="mt-4 flex items-center gap-2 rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 text-sm text-foreground">
      <Lock className="h-4 w-4 text-primary" />
      {children}
    </div>
  );
}

function saveDraft(draft: OnboardingDraft) {
  sessionStorage.setItem(env.onboardingDraftKey, JSON.stringify(draft));
}

function readDraft(): OnboardingDraft | null {
  try {
    const raw = sessionStorage.getItem(env.onboardingDraftKey);
    if (!raw) return null;
    return JSON.parse(raw) as OnboardingDraft;
  } catch {
    return null;
  }
}

function clearDraft() {
  sessionStorage.removeItem(env.onboardingDraftKey);
}

function Onboarding() {
  const navigate = useNavigate();
  const { ready, user, refresh, setSessionUser } = useAuth();
  const { auth, authError, access_token: accessToken } = Route.useSearch();
  const [config, setConfig] = useState<CreatorConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [videoType, setVideoType] = useState<VideoTypeId | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [custom, setCustom] = useState("");
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [voiceId, setVoiceId] = useState("");
  const [narrator, setNarrator] = useState("");
  const [videoStyle, setVideoStyle] = useState("documentary");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [finishingGoogle, setFinishingGoogle] = useState(false);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [loadingPreviewId, setLoadingPreviewId] = useState<string | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const previewReqRef = useRef(0);
  const [suggestedCategories, setSuggestedCategories] = useState<string[]>([]);
  const [suggestedTopics, setSuggestedTopics] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [categoriesSource, setCategoriesSource] = useState<string | null>(null);
  const [topicsSource, setTopicsSource] = useState<string | null>(null);
  const categoriesCacheKey = useRef<string | null>(null);
  const topicsCacheKey = useRef<string | null>(null);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCreatorConfig();
        if (cancelled) return;
        setConfig(data);
        const firstNarrator =
          data.uiNarrators.find((n) => !n.locked)?.id || data.uiNarrators[0]?.id || "cloak";
        const firstVoice =
          data.creatorVoices?.[0]?.id || data.voices?.[0]?.id || "";
        const firstStyle =
          data.videoStyles?.[0]?.id || "documentary";
        setNarrator((prev) => prev || firstNarrator);
        setVoiceId((prev) => prev || firstVoice);
        setVideoStyle((prev) => prev || firstStyle);
      } catch (err) {
        if (!cancelled) {
          setConfigError(
            err instanceof ApiError ? err.message : "Could not load onboarding config",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, user?.id]);

  const creatorVoices: CreatorVoice[] = config?.creatorVoices ?? [];
  const videoStyles: VideoStyle[] = config?.videoStyles ?? [];
  const categories =
    suggestedCategories.length > 0
      ? suggestedCategories
      : config?.categories ?? [];
  const topics =
    suggestedTopics.length > 0
      ? suggestedTopics
      : config?.defaultTopics ?? [];
  const stepTitles = config?.stepTitles ?? [];
  const passwordMin = config?.passwordMinLength;

  const loadSuggestedCategories = async (force = false) => {
    if (!videoType) return;
    const key = videoType;
    if (!force && categoriesCacheKey.current === key && suggestedCategories.length) {
      return;
    }
    setLoadingCategories(true);
    try {
      const res = await api<{ categories: string[]; source?: string }>(
        creatorOnboardingSuggestCategoriesPath(),
        { method: "POST", body: { videoType } },
      );
      const next = Array.isArray(res.categories) ? res.categories : [];
      setSuggestedCategories(next);
      setCategoriesSource(res.source || null);
      categoriesCacheKey.current = key;
      if (category && next.length && !next.includes(category)) {
        setCategory(null);
      }
    } catch (err) {
      const fallback = config?.categories ?? [];
      setSuggestedCategories(fallback);
      setCategoriesSource("fallback");
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Could not load niche suggestions — showing defaults",
      );
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadSuggestedTopics = async (force = false) => {
    if (!videoType) return;
    const niche = custom.trim() || category || "";
    if (!niche) return;
    const key = `${videoType}|${niche}`;
    if (!force && topicsCacheKey.current === key && suggestedTopics.length) {
      return;
    }
    setLoadingTopics(true);
    try {
      const res = await api<{ topics: string[]; source?: string }>(
        creatorOnboardingSuggestTopicsPath(),
        {
          method: "POST",
          body: {
            videoType,
            category,
            customTopic: custom.trim(),
          },
        },
      );
      const next = Array.isArray(res.topics) ? res.topics : [];
      setSuggestedTopics(next);
      setTopicsSource(res.source || null);
      topicsCacheKey.current = key;
      setSelectedTopics(next.slice());
    } catch (err) {
      const fallback =
        (category && config?.topicsByCategory?.[category]) ||
        config?.defaultTopics ||
        [];
      setSuggestedTopics(fallback);
      setTopicsSource("fallback");
      setSelectedTopics(fallback.slice());
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Could not generate episode topics — showing defaults",
      );
    } finally {
      setLoadingTopics(false);
    }
  };

  useEffect(() => {
    if (step !== 1 || !videoType) return;
    void loadSuggestedCategories(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when entering step 1 / videoType
  }, [step, videoType]);

  useEffect(() => {
    if (step !== 2 || !videoType) return;
    if (!(category || custom.trim())) return;
    void loadSuggestedTopics(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when entering step 2 / niche
  }, [step, videoType, category, custom]);

  const activeVoice =
    creatorVoices.find((v) => v.id === voiceId) || creatorVoices[0];
  const activeStyle =
    videoStyles.find((s) => s.id === videoStyle) || videoStyles[0];

  useEffect(() => {
    return () => {
      previewReqRef.current += 1;
      previewAudioRef.current?.pause();
      previewAudioRef.current = null;
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  const stopVoicePreview = () => {
    previewReqRef.current += 1;
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewingId(null);
    setLoadingPreviewId(null);
  };

  const playVoicePreview = async (id: string) => {
    if (previewingId === id) {
      stopVoicePreview();
      return;
    }
    const reqId = ++previewReqRef.current;
    setLoadingPreviewId(id);
    try {
      previewAudioRef.current?.pause();
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      const res = await fetch(
        `${env.apiUrl}/api/creator/voices/${encodeURIComponent(id)}/preview`,
        { credentials: "include" },
      );
      if (reqId !== previewReqRef.current) return;
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { error?: string }).error || "Could not load voice preview",
        );
      }
      const blob = await res.blob();
      if (reqId !== previewReqRef.current) return;
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      const audio = new Audio(url);
      previewAudioRef.current = audio;
      audio.onended = () => {
        if (reqId === previewReqRef.current) setPreviewingId(null);
      };
      await audio.play();
      if (reqId !== previewReqRef.current) return;
      setPreviewingId(id);
    } catch (err) {
      if (reqId !== previewReqRef.current) return;
      toast.error(
        err instanceof Error ? err.message : "Voice preview failed",
      );
    } finally {
      if (reqId === previewReqRef.current) setLoadingPreviewId(null);
    }
  };

  const videoTypeOptions = config?.videoTypes?.length
    ? config.videoTypes
    : [
        {
          id: "SHORTS",
          label: "Shorts",
          description:
            "Vertical 9:16 videos for YouTube Shorts, Reels, and TikTok.",
          aspect: "9:16",
        },
        {
          id: "LONGFORM",
          label: "Longform",
          description: "Landscape 16:9 videos for classic YouTube.",
          aspect: "16:9",
        },
      ];

  const draftPayload = (): OnboardingDraft => ({
    videoType,
    category,
    custom,
    selectedTopics,
    voiceId,
    narrator,
    videoStyle,
  });

  const onboardingBody = (draft?: OnboardingDraft) => {
    const d = draft ?? draftPayload();
    return {
      videoType: d.videoType || "SHORTS",
      category: d.category,
      customTopic: d.custom.trim(),
      topics: d.selectedTopics,
      voiceId: d.voiceId || undefined,
      narratorSlug: d.narrator || "cloak",
      videoStyle: d.videoStyle || "documentary",
    };
  };

  const persistSeries = async (draft?: OnboardingDraft) => {
    await api(env.paths.creatorOnboarding, {
      method: "POST",
      body: onboardingBody(draft),
    });
    clearDraft();
  };

  useEffect(() => {
    if (authError) {
      toast.error("Google sign-in failed. Try email instead.");
    }
  }, [authError]);

  useEffect(() => {
    if (auth !== "google") return;

    if (accessToken) {
      setAccessToken(accessToken);
      void refresh();
      navigate({
        to: env.routes.onboarding,
        search: { auth: "google" },
        replace: true,
      });
      return;
    }

    const draft = readDraft();
    if (!draft?.selectedTopics?.length || !draft?.videoType) {
      toast.message("Signed in. Finish the steps, then save your series.");
      setStep(Math.max(0, (config?.stepTitles.length || 1) - 1));
      return;
    }

    let cancelled = false;
    (async () => {
      setFinishingGoogle(true);
      try {
        setVideoType(draft.videoType);
        setCategory(draft.category);
        setCustom(draft.custom);
        setSelectedTopics(draft.selectedTopics);
        setVoiceId(draft.voiceId || "");
        setNarrator(draft.narrator || "cloak");
        setVideoStyle(draft.videoStyle || "documentary");
        await persistSeries(draft);
        if (!cancelled) {
          toast.success("Series saved — connect a social account when ready.");
          navigate({ to: env.routes.dashboard });
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof ApiError ? err.message : "Could not save your series";
          toast.error(message);
          setStep(Math.max(0, (config?.stepTitles.length || 6) - 1));
        }
      } finally {
        if (!cancelled) setFinishingGoogle(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on Google return
  }, [auth, accessToken]);

  const pickCategory = (c: string) => {
    setCategory(c);
    setCustom("");
    topicsCacheKey.current = null;
  };

  const canContinue =
    step === 0
      ? Boolean(videoType)
      : step === 1
        ? Boolean(category || custom.trim()) && !loadingCategories
        : step === 2
          ? selectedTopics.length > 0 && !loadingTopics
          : step === 3
            ? Boolean(voiceId)
            : step === 4
              ? Boolean(videoStyle)
              : true;

  const continueWithGoogle = () => {
    if (!selectedTopics.length) {
      toast.error("Pick at least one episode topic before continuing.");
      return;
    }
    if (!videoType) {
      toast.error("Choose Shorts or Longform before continuing.");
      return;
    }
    saveDraft(draftPayload());
    window.location.href = googleAuthUrl(env.routes.onboarding);
  };

  const isAuthed = Boolean(user);

  const saveSeriesForAuthedUser = async () => {
    if (!videoType) {
      toast.error("Choose Shorts or Longform first.");
      return;
    }
    if (!selectedTopics.length) {
      toast.error("Pick at least one episode topic.");
      return;
    }
    setSubmitting(true);
    try {
      await persistSeries();
      toast.success("Series saved — connect a social account when ready.");
      navigate({ to: env.routes.dashboard });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not save your series");
    } finally {
      setSubmitting(false);
    }
  };

  const createAccountAndSave = async () => {
    if (!config || passwordMin == null) {
      toast.error("Onboarding config is still loading.");
      return;
    }
    if (!email.trim() || password.length < passwordMin) {
      toast.error(`Enter an email and a password (at least ${passwordMin} characters).`);
      return;
    }
    if (!videoType) {
      toast.error("Choose Shorts or Longform first.");
      return;
    }
    if (!selectedTopics.length) {
      toast.error("Pick at least one episode topic.");
      return;
    }

    setSubmitting(true);
    try {
      let session: { user: AuthUser };
      try {
        session = await api<{ user: AuthUser }>(env.paths.authSignup, {
          method: "POST",
          body: { email: email.trim(), password },
        });
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          session = await api<{ user: AuthUser }>(env.paths.authLogin, {
            method: "POST",
            body: { email: email.trim(), password },
          });
        } else {
          throw err;
        }
      }
      setSessionUser(session.user);
      await refresh();

      await persistSeries();
      toast.success("Series saved — connect a social account when ready.");
      navigate({ to: env.routes.dashboard });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Could not create your account";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (configError) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SiteHeader />
        <main className="mx-auto max-w-3xl px-5 py-24 text-center text-destructive">
          {configError}
        </main>
      </div>
    );
  }

  if (!config || finishingGoogle) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SiteHeader />
        <main className="mx-auto max-w-3xl px-5 py-24 text-center text-muted-foreground">
          {finishingGoogle ? "Saving your series…" : "Loading…"}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 py-12">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Step {step + 1} of {stepTitles.length || 1}
          </span>
          <span>{stepTitles[step]}</span>
        </div>
        <Progress
          value={((step + 1) / (stepTitles.length || 1)) * 100}
          className="mt-3 h-1.5"
        />

        <h1 className="mt-8 text-3xl font-bold tracking-tight">{stepTitles[step]}</h1>

        {step === 0 && (
          <div className="mt-6">
            <p className="text-muted-foreground">
              Shorts are vertical. Longform is landscape for classic YouTube.
            </p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {videoTypeOptions.map((opt) => {
                const id = opt.id as VideoTypeId;
                const selected = videoType === id;
                const Icon = id === "LONGFORM" ? Monitor : Smartphone;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      setVideoType(id);
                      categoriesCacheKey.current = null;
                      topicsCacheKey.current = null;
                      setSuggestedCategories([]);
                      setSuggestedTopics([]);
                      setSelectedTopics([]);
                    }}
                    className={cn(
                      "relative rounded-2xl border p-5 text-left transition-all",
                      selected
                        ? "border-primary bg-primary/10 shadow-glow"
                        : "border-border bg-card hover:border-primary/60",
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div
                        className={cn(
                          "flex items-center justify-center rounded-xl border border-border bg-secondary/40",
                          id === "LONGFORM"
                            ? "h-14 w-24"
                            : "h-20 w-12",
                        )}
                      >
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      {selected && (
                        <span className="rounded-full bg-primary p-1.5">
                          <Check className="h-3.5 w-3.5 text-primary-foreground" />
                        </span>
                      )}
                    </div>
                    <p className="mt-4 text-lg font-semibold">{opt.label}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {opt.description}
                    </p>
                    <p className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">
                      {opt.aspect || (id === "LONGFORM" ? "16:9" : "9:16")}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="mt-6">
            <p className="text-muted-foreground">
              What should your{" "}
              {videoType === "LONGFORM" ? "longform" : "Shorts"} videos be about?
            </p>
            <Input
              value={custom}
              onChange={(e) => {
                setCustom(e.target.value);
                setCategory(null);
                topicsCacheKey.current = null;
              }}
              placeholder="e.g. Unsolved maritime mysteries"
              className="mt-3 h-12 text-base"
            />
            {custom.trim() && !isAuthed && (
              <LockedBanner>Custom topics unlock after sign up.</LockedBanner>
            )}
            <div className="mt-8 flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Or pick a suggested niche
                {categoriesSource === "ai" ? " (AI)" : ""}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={loadingCategories || !videoType}
                onClick={() => void loadSuggestedCategories(true)}
              >
                <RefreshCw
                  className={cn(
                    "mr-1 h-3.5 w-3.5",
                    loadingCategories && "animate-spin",
                  )}
                />
                Refresh
              </Button>
            </div>
            {loadingCategories && categories.length === 0 ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating niche ideas for{" "}
                {videoType === "LONGFORM" ? "longform" : "Shorts"}…
              </div>
            ) : (
              <div className="mt-3 flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => pickCategory(c)}
                    className={cn(
                      "rounded-full border px-4 py-2 text-sm transition-colors",
                      category === c
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-foreground hover:border-primary/60",
                    )}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="mt-6">
            <div className="flex items-start justify-between gap-3">
              <p className="text-muted-foreground">
                Episode ideas for{" "}
                <span className="text-foreground">
                  {custom.trim() || category || "your niche"}
                </span>
                {topicsSource === "ai" ? " · AI suggestions" : ""}.
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={loadingTopics || !videoType}
                onClick={() => void loadSuggestedTopics(true)}
              >
                <RefreshCw
                  className={cn(
                    "mr-1 h-3.5 w-3.5",
                    loadingTopics && "animate-spin",
                  )}
                />
                Refresh
              </Button>
            </div>
            {loadingTopics ? (
              <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating episode topics…
              </div>
            ) : (
              <div className="mt-5 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
                {topics.map((t) => {
                  const checked = selectedTopics.includes(t);
                  return (
                    <label
                      key={t}
                      className="flex cursor-pointer items-center gap-3 px-4 py-4 hover:bg-secondary/50"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) =>
                          setSelectedTopics((prev) =>
                            v ? [...prev, t] : prev.filter((x) => x !== t),
                          )
                        }
                      />
                      <span className="flex-1 text-sm">{t}</span>
                      {!isAuthed && (
                        <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </label>
                  );
                })}
              </div>
            )}
            {!isAuthed && (
              <LockedBanner>Editing topic text unlocks after sign up.</LockedBanner>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="mt-6">
            <p className="text-muted-foreground">
              Pick the voice that narrates your{" "}
              {videoType === "LONGFORM" ? "longform" : "Shorts"} episodes.
              Preview before you continue.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {creatorVoices.map((v) => {
                const selected = voiceId === v.id;
                const loading = loadingPreviewId === v.id;
                const playing = previewingId === v.id;
                return (
                  <div
                    key={v.id}
                    className={cn(
                      "rounded-2xl border p-4 transition-all",
                      selected
                        ? "border-primary bg-primary/10 shadow-glow"
                        : "border-border bg-card",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setVoiceId(v.id)}
                      className="flex w-full items-start gap-3 text-left"
                    >
                      <img
                        src={v.avatar}
                        alt=""
                        className="h-14 w-14 rounded-xl object-cover bg-secondary"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium">{v.name}</p>
                          {selected && (
                            <span className="rounded-full bg-primary p-1">
                              <Check className="h-3.5 w-3.5 text-primary-foreground" />
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs capitalize text-muted-foreground">
                          {v.gender}
                          {v.tones?.length ? ` · ${v.tones.join(", ")}` : ""}
                        </p>
                      </div>
                    </button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-3 w-full gap-1.5"
                      disabled={loading || (!!loadingPreviewId && !loading)}
                      onClick={() => void playVoicePreview(v.id)}
                    >
                      {loading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : playing ? (
                        <Pause className="h-3.5 w-3.5" />
                      ) : (
                        <Volume2 className="h-3.5 w-3.5" />
                      )}
                      {loading ? "Loading…" : playing ? "Stop" : "Preview"}
                    </Button>
                  </div>
                );
              })}
            </div>
            {!creatorVoices.length && (
              <p className="mt-4 text-sm text-destructive">
                No curated voices configured. Set CREATOR_VOICES_JSON on the server.
              </p>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="mt-6">
            <p className="text-muted-foreground">
              This sets the visual and narrative flavor for stock footage and
              pacing — it affects real episode generation.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {videoStyles.map((style) => {
                const selected = videoStyle === style.id;
                return (
                  <button
                    key={style.id}
                    type="button"
                    onClick={() => setVideoStyle(style.id)}
                    className={cn(
                      "rounded-2xl border px-4 py-4 text-left transition-colors",
                      selected
                        ? "border-primary bg-primary/10 shadow-glow"
                        : "border-border bg-card hover:border-primary/40",
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium">{style.name}</p>
                      {selected && (
                        <span className="rounded-full bg-primary p-1">
                          <Check className="h-3.5 w-3.5 text-primary-foreground" />
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {style.blurb}
                    </p>
                  </button>
                );
              })}
            </div>
            {(activeVoice || activeStyle) && (
              <p className="mt-5 text-sm text-muted-foreground">
                {activeVoice?.name ? (
                  <>
                    Voice:{" "}
                    <span className="text-foreground">{activeVoice.name}</span>
                    {" · "}
                  </>
                ) : null}
                Style:{" "}
                <span className="text-foreground">
                  {activeStyle?.name || videoStyle}
                </span>
                {" · "}
                {videoType === "LONGFORM" ? "16:9 longform" : "9:16 Shorts"}
              </p>
            )}
            {!videoStyles.length && (
              <p className="mt-4 text-sm text-destructive">
                No video styles configured. Set CREATOR_VIDEO_STYLES_JSON on the
                server.
              </p>
            )}
          </div>
        )}

        {step === stepTitles.length - 1 && (
          <div className="mt-6">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <h2 className="flex items-center gap-2 font-semibold">
                <Sparkles className="h-4 w-4 text-primary" /> Your series is ready to save
              </h2>
              {isAuthed && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Signed in as {user?.email}
                  {config.plan ? ` · ${config.plan}` : ""}
                </p>
              )}
              <dl className="mt-5 space-y-3 text-sm">
                {[
                  [
                    "Video type",
                    videoType === "LONGFORM"
                      ? "Longform (16:9)"
                      : videoType === "SHORTS"
                        ? "Shorts (9:16)"
                        : "—",
                  ],
                  ["Topic", custom.trim() || category || "—"],
                  ["Episodes queued", String(selectedTopics.length)],
                  ["Voice", activeVoice?.name || voiceId || "—"],
                  ["Video style", activeStyle?.name || videoStyle || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-border/60 pb-2">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="font-medium">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-6 space-y-3">
                {isAuthed ? (
                  <Button
                    className="w-full"
                    size="lg"
                    disabled={submitting}
                    onClick={() => void saveSeriesForAuthedUser()}
                  >
                    <Sparkles className="mr-1 h-4 w-4" />
                    {submitting ? "Saving…" : "Save series"}
                  </Button>
                ) : (
                  <>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      className="h-11"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <PasswordInput
                      autoComplete="new-password"
                      placeholder={`Password (min ${passwordMin} characters)`}
                      className="h-11"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <Button
                      className="w-full"
                      size="lg"
                      disabled={submitting}
                      onClick={createAccountAndSave}
                    >
                      <Mail className="mr-1 h-4 w-4" />
                      {submitting ? "Saving…" : "Create account & save series"}
                    </Button>
                    <Button
                      variant="secondary"
                      className="w-full"
                      size="lg"
                      disabled={submitting}
                      onClick={continueWithGoogle}
                    >
                      Continue with Google
                    </Button>
                  </>
                )}
              </div>
              <p className="mt-4 text-center text-xs text-muted-foreground">
                Your series starts paused until you connect a social account.
              </p>
            </div>
          </div>
        )}

        <div className="mt-10 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || submitting}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          {step < stepTitles.length - 1 && (
            <Button disabled={!canContinue} onClick={() => setStep((s) => s + 1)}>
              Continue <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
